import React, { useMemo, useState } from 'react'

import { Link } from '@application/Interfaces/Atoms/Link'
import { OrganizationLogo } from '@application/Interfaces/Atoms/OrganizationLogo'
import { useGetHeader } from '@application/Interfaces/Hooks/useGetHeader'
import { useOrganization } from '@application/Interfaces/Hooks/useOrganization/useOrganization'
import { useTranslation } from '@application/Interfaces/Hooks/useTranslation'
import { CrossIcon, MenuBarIcon } from '@application/Interfaces/Icons'
import { AlternateUrlType, HeaderThemeModeType, NavigationThemeType } from '@application/Types/ComponentTypes'
import type { Locale } from '@application/Types/Locale'
import { Nullable } from '@application/Types/Opt'

import {
    DekstopNavbar,
    Drawer,
    HeaderContainer,
    HeaderContent,
    HeaderWrapper,
    LanguageSelectionMenu,
    LogoWrapper,
    MobileHeader,
    MobileNavbar,
    OpenMenu,
    RightSidebarWrapper,
} from './Header.styled'
import { MobileNav } from './MobileNav/MobileNav'
import {
    getColorBasedMode,
    getLastNavNode,
    getMobileNav,
    getNavNode,
    handleTransitionExited,
    PendingNavigation,
    toggleMenu,
} from './utils'

type HeaderProps = {
    language?: Locale
    mode: HeaderThemeModeType
    type: NavigationThemeType
    alternateUrls?: Nullable<AlternateUrlType[]>
}

export const Header = ({ language = 'en', mode = 'Light', type, alternateUrls }: HeaderProps): JSX.Element => {
    const header = useGetHeader(language)
    const { t } = useTranslation()

    const leftNav = useMemo(() => getNavNode(header, 'left'), [header])

    const rightNav = useMemo(() => getNavNode(header, 'right'), [header])
    const organization = useOrganization()

    const lastRightNavItem = useMemo(() => getLastNavNode(rightNav), [rightNav])
    const [isExpanded, setIsExpanded] = useState(false)
    const [pendingNavigation, setPendingNavigation] = useState<PendingNavigation>(null)

    const mobileNavItems = useMemo(
        () =>
            getMobileNav({
                leftItems: leftNav,
                rightItems: rightNav,
                language,
                languageLabel: `${t('language')}: ${language.toUpperCase()}`,
                alternateUrls,
                organizationLanguages: organization.languages,
                setPendingNavigation,
                setIsExpanded,
            }),
        [alternateUrls, leftNav, rightNav, language, t]
    )

    const colorCode = useMemo(() => getColorBasedMode(mode), [mode])

    return (
        <HeaderWrapper>
            <HeaderContainer $mode={mode}>
                <HeaderContent>
                    <LogoWrapper>
                        <Link to={language === 'en' ? '/' : `/${language}/`}>
                            <OrganizationLogo
                                mode={mode}
                                width={organization.logoDimensions.header.width}
                                height={organization.logoDimensions.header.height}
                            />
                        </Link>
                    </LogoWrapper>
                    {type !== 'Minimal' && (
                        <>
                            <DekstopNavbar nodes={leftNav} className={mode} mode={mode} type={type} />
                            <Drawer
                                open={isExpanded}
                                onClose={toggleMenu(setIsExpanded, false)}
                                hideBackdrop
                                disableScrollLock
                                onTransitionExited={handleTransitionExited({
                                    pendingNavigation,
                                    setPendingNavigation,
                                })}
                            >
                                <MobileHeader>
                                    <Link to={language === 'en' ? '/' : `/${language}/`}>
                                        <OrganizationLogo
                                            mode="Light"
                                            width={organization.logoDimensions.header.width}
                                            height={organization.logoDimensions.header.height}
                                        />
                                    </Link>
                                    <OpenMenu
                                        colorStyle="ghost"
                                        className="menu-toggle"
                                        icon={<CrossIcon width={15} height={15} colorCode="g500" />}
                                        onClick={toggleMenu(setIsExpanded, !isExpanded)}
                                        data-testid="crossButtonId"
                                    />
                                </MobileHeader>
                                <MobileNav nodes={mobileNavItems} />
                            </Drawer>
                        </>
                    )}
                    <RightSidebarWrapper>
                        {type !== 'Minimal' && <LanguageSelectionMenu mode={mode} alternateUrls={alternateUrls} />}
                        <DekstopNavbar
                            nodes={type === 'Minimal' ? [rightNav[rightNav.length - 1]] : rightNav}
                            className="right"
                            mode={mode}
                            type={type}
                        />
                        {lastRightNavItem && (
                            <MobileNavbar nodes={[lastRightNavItem]} className="right" mode={mode} type={type} />
                        )}
                        {type !== 'Minimal' && (
                            <OpenMenu
                                colorStyle="ghost"
                                className="menu-toggle"
                                icon={<MenuBarIcon colorCode={colorCode} />}
                                onClick={toggleMenu(setIsExpanded, !isExpanded)}
                                data-testid="menuButtonId"
                            />
                        )}
                    </RightSidebarWrapper>
                </HeaderContent>
            </HeaderContainer>
        </HeaderWrapper>
    )
}
